/**
 * Club role / capability model.
 *
 * Single source of truth for "who can do what" in a club. Templates and
 * component logic consult `ClubService.can()` (and the convenience getters);
 * the Cloud Functions in functions/bvapi.js mirror this matrix server-side.
 *
 * Pure and dependency-free on purpose so it can be reasoned about (and copied
 * to the functions side) in isolation.
 *
 * The role *ladder* (highest to lowest): Owner > Director > Floor >
 * TableCaptain > Member. Note the stored role string for the Director tier is
 * still "Staff" (the historical value) — no data migration; the "Director"
 * label is applied via i18n in ClubService.roles.
 */

export const ClubRole = {
  Owner: "Owner",
  /** Stored as "Staff"; displayed as "Director". */
  Staff: "Staff",
  Floor: "Floor",
  TableCaptain: "TableCaptain",
  Member: "Member",
} as const;
export type ClubRole = (typeof ClubRole)[keyof typeof ClubRole];

/** Stored role -> i18n key for its display label (Staff shows as "Director"). */
export const ROLE_LABEL_KEY: Record<ClubRole, string> = {
  Owner: "OWNER",
  Staff: "DIRECTOR",
  Floor: "FLOOR",
  TableCaptain: "TABLE_CAPTAIN",
  Member: "MEMBER",
};

/** Used for display ordering. Assignment is no longer rank-gated (see
 *  ClubService.assignableRoles) — any member-manager can assign Director-and-down. */
export const ROLE_RANK: Record<ClubRole, number> = {
  Owner: 50,
  Staff: 40,
  Floor: 30,
  TableCaptain: 20,
  Member: 10,
};

export const Capability = {
  // Irreversible entity-deletes — Owner only.
  DELETE_CLUB: "DELETE_CLUB",
  DELETE_MEMBER: "DELETE_MEMBER",
  DELETE_TOURNAMENT: "DELETE_TOURNAMENT",
  DELETE_LEAGUE: "DELETE_LEAGUE",
  // Configuration + administration — Director+.
  CONFIG_TOURNAMENT: "CONFIG_TOURNAMENT",
  CONFIG_LEAGUE: "CONFIG_LEAGUE",
  CONFIG_LOCATION: "CONFIG_LOCATION",
  CONFIG_CLUB: "CONFIG_CLUB",
  MANAGE_MEMBERS: "MANAGE_MEMBERS",
  UNDO: "UNDO",
  // Operational live-tournament control — Floor+.
  CLOCK_CONTROL: "CLOCK_CONTROL",
  SEATING_OPS: "SEATING_OPS",
  REGISTER_PLAYERS: "REGISTER_PLAYERS",
  PRINT_TICKET: "PRINT_TICKET",
  // Eliminations — Floor+ and Table Captain.
  ELIMINATE_PLAYER: "ELIMINATE_PLAYER",
  // Correct a player's chip stack — Floor+ and Table Captain (table-side
  // count fixes; distinct from rebuy/add-on which stay Floor+).
  EDIT_STACK: "EDIT_STACK",
} as const;
export type Capability = (typeof Capability)[keyof typeof Capability];

const OWNER_ONLY: Capability[] = [
  Capability.DELETE_CLUB,
  Capability.DELETE_MEMBER,
  Capability.DELETE_TOURNAMENT,
  Capability.DELETE_LEAGUE,
];

const FLOOR_CAPS: Capability[] = [
  Capability.CLOCK_CONTROL,
  Capability.SEATING_OPS,
  Capability.REGISTER_PLAYERS,
  Capability.PRINT_TICKET,
  Capability.ELIMINATE_PLAYER,
  Capability.EDIT_STACK,
];

const STAFF_CAPS: Capability[] = [
  Capability.CONFIG_TOURNAMENT,
  Capability.CONFIG_LEAGUE,
  Capability.CONFIG_LOCATION,
  Capability.CONFIG_CLUB,
  Capability.MANAGE_MEMBERS,
  Capability.UNDO,
  ...FLOOR_CAPS,
];

/** Role -> capabilities. Spreads make the inheritance explicit. */
export const ROLE_CAPS: Record<ClubRole, ReadonlySet<Capability>> = {
  Owner: new Set<Capability>([...OWNER_ONLY, ...STAFF_CAPS]),
  Staff: new Set<Capability>([...STAFF_CAPS]),
  Floor: new Set<Capability>([...FLOOR_CAPS]),
  TableCaptain: new Set<Capability>([
    Capability.ELIMINATE_PLAYER,
    Capability.EDIT_STACK,
  ]),
  Member: new Set<Capability>(),
};

/** O(1) lookup; fail-closed on null/unknown role. */
export function roleHasCapability(
  role: ClubRole | null | undefined,
  cap: Capability
): boolean {
  if (!role) return false;
  return ROLE_CAPS[role]?.has(cap) ?? false;
}

// ---------------------------------------------------------------------------
// Club WEBSITE roles (Blind Valet Sites). Independent of the club role above:
// a site is its own entity (owned by a uid, possibly spanning several clubs),
// so its roles live on the site — clubsites/{siteID}/team/{uid}/role — and a
// plain club Member can be a site Contributor. The site owner (ownerUid, the
// paying account) is implicit and above the ladder. No implicit rights from
// club roles: explicit team only. Mirrored server-side in
// functions/clubsites.js (SITE_ROLE_CAPS / resolveSiteRole).
//
// Ladder (highest to lowest): Owner > Webmaster > Editor > Contributor.
// ---------------------------------------------------------------------------

export const SiteRole = {
  /** Implicit: clubsites/{id}/ownerUid. Never stored under team/. */
  Owner: "Owner",
  Webmaster: "Webmaster",
  Editor: "Editor",
  Contributor: "Contributor",
} as const;
export type SiteRole = (typeof SiteRole)[keyof typeof SiteRole];

/** Roles that can be assigned to a team member (everything but Owner). */
export const ASSIGNABLE_SITE_ROLES: SiteRole[] = [
  SiteRole.Webmaster,
  SiteRole.Editor,
  SiteRole.Contributor,
];

export const SITE_ROLE_LABEL_KEY: Record<SiteRole, string> = {
  Owner: "OWNER",
  Webmaster: "SITE_ROLE_WEBMASTER",
  Editor: "SITE_ROLE_EDITOR",
  Contributor: "SITE_ROLE_CONTRIBUTOR",
};

export const SiteCapability = {
  // Owner only: money, the site's existence, which clubs feed it.
  SITE_BILLING: "SITE_BILLING",
  DELETE_SITE: "DELETE_SITE",
  SITE_CLUBS: "SITE_CLUBS",
  // Webmaster+: the site as a product (look, identity, address, who edits).
  MANAGE_TEAM: "MANAGE_TEAM",
  SITE_DESIGN: "SITE_DESIGN",
  SITE_SETTINGS: "SITE_SETTINGS",
  SITE_DOMAIN: "SITE_DOMAIN",
  SITE_ONLINE: "SITE_ONLINE",
  // Editor+: content — pages/drafts, publishing versions, every article.
  SITE_PAGES: "SITE_PAGES",
  SITE_PUBLISH: "SITE_PUBLISH",
  ARTICLES_ALL: "ARTICLES_ALL",
  // Contributor+: own articles (never published by them).
  ARTICLES_OWN: "ARTICLES_OWN",
} as const;
export type SiteCapability =
  (typeof SiteCapability)[keyof typeof SiteCapability];

const CONTRIBUTOR_SITE_CAPS: SiteCapability[] = [SiteCapability.ARTICLES_OWN];

const EDITOR_SITE_CAPS: SiteCapability[] = [
  SiteCapability.SITE_PAGES,
  SiteCapability.SITE_PUBLISH,
  SiteCapability.ARTICLES_ALL,
  ...CONTRIBUTOR_SITE_CAPS,
];

const WEBMASTER_SITE_CAPS: SiteCapability[] = [
  SiteCapability.MANAGE_TEAM,
  SiteCapability.SITE_DESIGN,
  SiteCapability.SITE_SETTINGS,
  SiteCapability.SITE_DOMAIN,
  SiteCapability.SITE_ONLINE,
  ...EDITOR_SITE_CAPS,
];

/** Site role -> capabilities. KEEP IN SYNC with functions/clubsites.js. */
export const SITE_ROLE_CAPS: Record<SiteRole, ReadonlySet<SiteCapability>> = {
  Owner: new Set<SiteCapability>([
    SiteCapability.SITE_BILLING,
    SiteCapability.DELETE_SITE,
    SiteCapability.SITE_CLUBS,
    ...WEBMASTER_SITE_CAPS,
  ]),
  Webmaster: new Set<SiteCapability>([...WEBMASTER_SITE_CAPS]),
  Editor: new Set<SiteCapability>([...EDITOR_SITE_CAPS]),
  Contributor: new Set<SiteCapability>([...CONTRIBUTOR_SITE_CAPS]),
};

/**
 * A Blind Valet admin while bvadmin's Take Control holds the site's primary
 * club: the owner's capabilities except billing and deletion. Not a role
 * (nothing is stored); ClubsiteService.can() adds it. KEEP IN SYNC with
 * ADMIN_SITE_CAPS in functions/clubsites.js.
 */
export const ADMIN_SITE_CAPS: ReadonlySet<SiteCapability> = new Set<SiteCapability>([
  SiteCapability.SITE_CLUBS,
  ...WEBMASTER_SITE_CAPS,
]);

/** O(1) lookup; fail-closed on null/unknown role. */
export function siteRoleHasCapability(
  role: SiteRole | null | undefined,
  cap: SiteCapability
): boolean {
  if (!role) return false;
  return SITE_ROLE_CAPS[role]?.has(cap) ?? false;
}
